/** Dimension + fill parameter form for the active fill kind. */
import type {
  Design,
  EccentricFill,
  Fill,
  HomogeneousFill,
  RadialFill,
  ShellWedgeFill,
  WedgeFill,
} from "../model/fill";
import { lastSectorDeg } from "../model/fill";
import { design, updateDesign } from "../state/store";
import { NumberField, Select } from "./common";
import { DielectricEdit } from "./MaterialSelect";
import { dielFromPreset } from "../model/materials";

function setFill<F extends Fill>(mutate: (f: F) => F): void {
  updateDesign((d: Design) => ({ ...d, fill: mutate(d.fill as F) }));
}

function DimField(props: { label: string; path: string; value: number; onChange: (v: number) => void }) {
  const unit = design.value.unit;
  return <NumberField label={props.label} unit={unit} path={props.path} value={props.value} onChange={props.onChange} step={0.01} />;
}

function HomogeneousForm({ f }: { f: HomogeneousFill }) {
  return (
    <>
      <DimField label="Inner radius a" path="a" value={f.a} onChange={(a) => setFill<HomogeneousFill>((x) => ({ ...x, a }))} />
      <DimField label="Outer radius b" path="b" value={f.b} onChange={(b) => setFill<HomogeneousFill>((x) => ({ ...x, b }))} />
      <div class="field">
        <span class="field-label">Dielectric</span>
        <DielectricEdit path="diel" value={f.diel} onChange={(diel) => setFill<HomogeneousFill>((x) => ({ ...x, diel }))} />
      </div>
    </>
  );
}

function RadialForm({ f }: { f: RadialFill }) {
  return (
    <>
      <DimField label="Inner radius a" path="a" value={f.a} onChange={(a) => setFill<RadialFill>((x) => ({ ...x, a }))} />
      <DimField label="Outer radius b" path="b" value={f.b} onChange={(b) => setFill<RadialFill>((x) => ({ ...x, b }))} />
      <div class="list-editor">
        <div class="list-head">
          <span>Layers (inside → out)</span>
          <button
            class="mini"
            title="Add a layer"
            onClick={() =>
              setFill<RadialFill>((x) => {
                const last = x.layers[x.layers.length - 1]!;
                const prevR = x.layers.length >= 2 ? x.layers[x.layers.length - 2]!.rOuter : x.a;
                const mid = (prevR + x.b) / 2;
                const layers = [...x.layers.slice(0, -1), { rOuter: mid, diel: dielFromPreset("ptfe") }, last];
                return { ...x, layers };
              })
            }
          >
            +
          </button>
        </div>
        <div class="list-row list-header-row">
          <span class="col-r">r · {design.value.unit}</span>
          <span>material · εr · tanδ</span>
          <span />
        </div>
        {f.layers.map((l, i) => {
          const isLast = i === f.layers.length - 1;
          return (
            <div class="list-row" key={i}>
              {isLast ? (
                <span class="col-r pinned" title="The last layer extends to the shield">
                  = b
                </span>
              ) : (
                <input
                  class="col-r"
                  type="number"
                  step={0.01}
                  value={l.rOuter}
                  onInput={(e) => {
                    const v = parseFloat((e.target as HTMLInputElement).value);
                    if (Number.isFinite(v))
                      setFill<RadialFill>((x) => ({
                        ...x,
                        layers: x.layers.map((y, j) => (j === i ? { ...y, rOuter: v } : y)),
                      }));
                  }}
                />
              )}
              <DielectricEdit
                compact
                path={`layers.${i}.diel`}
                value={l.diel}
                onChange={(diel) => setFill<RadialFill>((x) => ({ ...x, layers: x.layers.map((y, j) => (j === i ? { ...y, diel } : y)) }))}
              />
              <button
                class="mini danger"
                title="Remove layer"
                disabled={f.layers.length <= 2}
                onClick={() => setFill<RadialFill>((x) => ({ ...x, layers: x.layers.filter((_, j) => j !== i) }))}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

function WedgeForm({ f }: { f: WedgeFill }) {
  return (
    <>
      <DimField label="Inner radius a" path="a" value={f.a} onChange={(a) => setFill<WedgeFill>((x) => ({ ...x, a }))} />
      <DimField label="Outer radius b" path="b" value={f.b} onChange={(b) => setFill<WedgeFill>((x) => ({ ...x, b }))} />
      <div class="list-editor">
        <div class="list-head">
          <span>Sectors (CCW from θ = 0)</span>
          <button
            class="mini"
            title="Add a sector"
            onClick={() =>
              setFill<WedgeFill>((x) => {
                const last = x.sectors[x.sectors.length - 1]!;
                const sectors = [...x.sectors.slice(0, -1), { phiDeg: 45, diel: dielFromPreset("ptfe") }, last];
                return { ...x, sectors };
              })
            }
          >
            +
          </button>
        </div>
        <div class="list-row list-header-row">
          <span class="col-r">φ · deg</span>
          <span>material · εr · tanδ</span>
          <span />
        </div>
        {f.sectors.map((s, i) => {
          const isLast = i === f.sectors.length - 1;
          return (
            <div class="list-row" key={i}>
              {isLast ? (
                <span class="col-r pinned" title="Balance: 360° minus the other sectors">
                  {lastSectorDeg(f).toFixed(1)}°
                </span>
              ) : (
                <input
                  class="col-r"
                  type="number"
                  step={5}
                  value={s.phiDeg}
                  onInput={(e) => {
                    const v = parseFloat((e.target as HTMLInputElement).value);
                    if (Number.isFinite(v))
                      setFill<WedgeFill>((x) => ({
                        ...x,
                        sectors: x.sectors.map((y, j) => (j === i ? { ...y, phiDeg: v } : y)),
                      }));
                  }}
                />
              )}
              <DielectricEdit
                compact
                path={`sectors.${i}.diel`}
                value={s.diel}
                onChange={(diel) => setFill<WedgeFill>((x) => ({ ...x, sectors: x.sectors.map((y, j) => (j === i ? { ...y, diel } : y)) }))}
              />
              <button
                class="mini danger"
                title="Remove sector"
                disabled={f.sectors.length <= 2}
                onClick={() => setFill<WedgeFill>((x) => ({ ...x, sectors: x.sectors.filter((_, j) => j !== i) }))}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ShellWedgeForm({ f }: { f: ShellWedgeFill }) {
  const quad = (key: "e1" | "e2" | "e3" | "e4", label: string) => (
    <div class="field">
      <span class="field-label">{label}</span>
      <DielectricEdit compact path={key} value={f[key]} onChange={(v) => setFill<ShellWedgeFill>((x) => ({ ...x, [key]: v }))} />
    </div>
  );
  return (
    <>
      <DimField label="Inner radius a" path="a" value={f.a} onChange={(a) => setFill<ShellWedgeFill>((x) => ({ ...x, a }))} />
      <DimField label="Shell radius c" path="c" value={f.c} onChange={(c) => setFill<ShellWedgeFill>((x) => ({ ...x, c }))} />
      <DimField label="Outer radius b" path="b" value={f.b} onChange={(b) => setFill<ShellWedgeFill>((x) => ({ ...x, b }))} />
      <NumberField label="Wedge angle φ" unit="deg" path="phiDeg" step={5} value={f.phiDeg} onChange={(phiDeg) => setFill<ShellWedgeFill>((x) => ({ ...x, phiDeg }))} />
      {quad("e1", "ε1 (wedge, inner)")}
      {quad("e2", "ε2 (wedge, outer)")}
      {quad("e3", "ε3 (rest, inner)")}
      {quad("e4", "ε4 (rest, outer)")}
    </>
  );
}

function EccentricForm({ f }: { f: EccentricFill }) {
  return (
    <>
      <DimField label="Inner radius a" path="a" value={f.a} onChange={(a) => setFill<EccentricFill>((x) => ({ ...x, a }))} />
      <DimField label="Outer radius b" path="b" value={f.b} onChange={(b) => setFill<EccentricFill>((x) => ({ ...x, b }))} />
      <DimField label="Offset d" path="d" value={f.d} onChange={(d) => setFill<EccentricFill>((x) => ({ ...x, d }))} />
      <label class="field checkbox-field">
        <input
          type="checkbox"
          checked={!!f.twoLayer}
          onChange={(e) => {
            const on = (e.target as HTMLInputElement).checked;
            setFill<EccentricFill>((x) => ({
              ...x,
              twoLayer: on ? { t: 0.5, inner: x.diel, outer: dielFromPreset("air") } : null,
            }));
          }}
        />
        <span>Two-layer fill (conformal interface)</span>
      </label>
      {f.twoLayer ? (
        <>
          <NumberField
            label="Interface fraction t"
            path="twoLayer.t"
            step={0.05}
            value={f.twoLayer.t}
            onChange={(t) => setFill<EccentricFill>((x) => ({ ...x, twoLayer: { ...x.twoLayer!, t } }))}
          />
          <div class="field">
            <span class="field-label">Inner layer</span>
            <DielectricEdit compact path="twoLayer.inner" value={f.twoLayer.inner} onChange={(v) => setFill<EccentricFill>((x) => ({ ...x, twoLayer: { ...x.twoLayer!, inner: v } }))} />
          </div>
          <div class="field">
            <span class="field-label">Outer layer</span>
            <DielectricEdit compact path="twoLayer.outer" value={f.twoLayer.outer} onChange={(v) => setFill<EccentricFill>((x) => ({ ...x, twoLayer: { ...x.twoLayer!, outer: v } }))} />
          </div>
        </>
      ) : (
        <div class="field">
          <span class="field-label">Dielectric</span>
          <DielectricEdit path="diel" value={f.diel} onChange={(diel) => setFill<EccentricFill>((x) => ({ ...x, diel }))} />
        </div>
      )}
    </>
  );
}

export function ParamForm() {
  const f = design.value.fill;
  return (
    <div class="param-form">
      <Select
        label="Unit"
        value={design.value.unit}
        options={[
          { value: "mm", label: "mm" },
          { value: "um", label: "µm" },
          { value: "mil", label: "mil" },
        ]}
        onChange={(unit) => updateDesign((d) => ({ ...d, unit }))}
      />
      {f.kind === "homogeneous" && <HomogeneousForm f={f} />}
      {f.kind === "radial" && <RadialForm f={f} />}
      {f.kind === "wedge" && <WedgeForm f={f} />}
      {f.kind === "shellWedge" && <ShellWedgeForm f={f} />}
      {f.kind === "eccentric" && <EccentricForm f={f} />}
    </div>
  );
}
